import { Link } from "@tanstack/react-router";
import { useState } from "react";
import { Menu, X, ChevronDown } from "lucide-react";
import logoMark from "@/assets/logo-rrlex-mark.webp";
import { useT, useMedia } from "@/lib/i18n";
import { LanguageSwitcher } from "@/components/site/LanguageSwitcher";
import siteConfig from "@/config/site.json";
import navigation from "@/config/navigation.json";

type NavChild = {
  to: string;
  labelKey: string;
  labelDefault: string;
  descKey?: string;
  descDefault?: string;
};

type NavLink = {
  to: string;
  labelKey: string;
  labelDefault: string;
  children?: NavChild[];
};

export function Nav() {
  const [open, setOpen] = useState(false);
  const [mobileExpanded, setMobileExpanded] = useState<string | null>(null);
  const { tk } = useT();
  const logo = useMedia("brand.logo.mark", logoMark, siteConfig.brand.name);

  const links: NavLink[] = navigation.primary as NavLink[];
  const cta = {
    to: navigation.cta.to as "/contact",
    label: tk(navigation.cta.labelKey, navigation.cta.labelDefault),
  };

  return (
    <header className="sticky top-0 z-50 backdrop-blur-md bg-background/80 border-b border-gold/20">
      <div className="max-w-7xl mx-auto flex items-center justify-between px-6 py-5">
        <Link to="/" className="relative flex items-center" aria-label={siteConfig.brand.name}>
          <span className="absolute left-0 -top-4 flex items-center justify-center h-24 w-24 md:h-32 md:w-32 lg:h-36 lg:w-36 z-10 bg-black rounded-full border-none">
            <img src={logo.url} alt={logo.alt} width={144} height={144} />
          </span>
          <span className="block h-10 md:h-12 lg:h-14" style={{ width: "9rem" }} aria-hidden />
        </Link>

        <nav className="hidden md:flex items-center gap-10">
          {links.map((l) => {
            const label = tk(l.labelKey, l.labelDefault);
            if (!l.children || l.children.length === 0) {
              return (
                <Link
                  key={l.to}
                  to={l.to as "/"}
                  className="text-xs tracking-[0.25em] uppercase text-foreground/70 hover:text-gold transition-colors"
                  activeProps={{ className: "text-gold" }}
                >
                  {label}
                </Link>
              );
            }
            return (
              <div key={l.to} className="relative group">
                <Link
                  to={l.to as "/"}
                  className="inline-flex items-center gap-1.5 text-xs tracking-[0.25em] uppercase text-foreground/70 hover:text-gold transition-colors group-hover:text-gold"
                  activeProps={{ className: "text-gold" }}
                >
                  {label}
                  <ChevronDown size={12} className="opacity-70 transition-transform group-hover:rotate-180" />
                </Link>
                <div
                  className="absolute left-1/2 -translate-x-1/2 top-full pt-4 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-150 z-50"
                  role="menu"
                >
                  <div className="w-[320px] rounded-xl border border-gold/30 bg-background/95 backdrop-blur-md shadow-2xl shadow-black/50 p-2">
                    {l.children.map((c) => (
                      <Link
                        key={c.to}
                        to={c.to as "/"}
                        className="block rounded-lg px-4 py-3 hover:bg-gold/10 transition-colors"
                        activeProps={{ className: "bg-gold/10" }}
                      >
                        <div className="text-xs tracking-[0.25em] uppercase text-gold">
                          {tk(c.labelKey, c.labelDefault)}
                        </div>
                        {c.descDefault && (
                          <div className="text-xs text-muted-foreground mt-1.5 normal-case tracking-normal leading-relaxed">
                            {tk(c.descKey ?? c.labelKey + ".desc", c.descDefault)}
                          </div>
                        )}
                      </Link>
                    ))}
                  </div>
                </div>
              </div>
            );
          })}
        </nav>

        <div className="hidden md:flex items-center gap-4">
          <Link
            to={cta.to}
            className="inline-flex rounded-full border border-gold/60 text-gold text-xs tracking-[0.25em] uppercase px-6 py-2.5 hover:bg-gold hover:text-primary-foreground transition-colors"
          >
            {cta.label}
          </Link>
          <LanguageSwitcher />
        </div>

        <div className="flex md:hidden items-center gap-3">
          <button aria-label="Menu" className="text-gold" onClick={() => setOpen((v) => !v)}>
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {open && (
        <div className="md:hidden border-t border-gold/20 bg-background/95">
          <div className="px-6 py-6 flex flex-col gap-1">
            {links.map((l) => {
              const label = tk(l.labelKey, l.labelDefault);
              if (!l.children || l.children.length === 0) {
                return (
                  <Link
                    key={l.to}
                    to={l.to as "/"}
                    onClick={() => setOpen(false)}
                    className="block py-3 text-sm tracking-[0.2em] uppercase text-foreground/80 hover:text-gold border-b border-gold/10"
                  >
                    {label}
                  </Link>
                );
              }
              const expanded = mobileExpanded === l.to;
              return (
                <div key={l.to} className="border-b border-gold/10">
                  <div className="flex items-center justify-between py-3">
                    <Link
                      to={l.to as "/"}
                      onClick={() => setOpen(false)}
                      className="text-sm tracking-[0.2em] uppercase text-foreground/80 hover:text-gold"
                    >
                      {label}
                    </Link>
                    <button
                      type="button"
                      aria-label="Toggle submenu"
                      aria-expanded={expanded}
                      onClick={() => setMobileExpanded(expanded ? null : l.to)}
                      className="p-2 -mr-2 text-gold/80"
                    >
                      <ChevronDown
                        size={16}
                        className={`transition-transform ${expanded ? "rotate-180" : ""}`}
                      />
                    </button>
                  </div>
                  {expanded && (
                    <ul className="pb-3 pl-4 flex flex-col gap-1">
                      {l.children.map((c) => (
                        <li key={c.to}>
                          <Link
                            to={c.to as "/"}
                            onClick={() => setOpen(false)}
                            className="block py-2 text-xs tracking-[0.2em] uppercase text-foreground/70 hover:text-gold"
                          >
                            {tk(c.labelKey, c.labelDefault)}
                          </Link>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              );
            })}
            <div className="mt-5 flex items-center justify-between gap-4">
              <Link
                to={cta.to}
                onClick={() => setOpen(false)}
                className="inline-flex rounded-full border border-gold/60 text-gold text-xs tracking-[0.25em] uppercase px-5 py-2 hover:bg-gold hover:text-primary-foreground transition-colors"
              >
                {cta.label}
              </Link>
              <LanguageSwitcher />
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
